import React, { useEffect, useRef } from "react";
import axios from "axios";
import { Modal } from "antd";
import { getTimeZone } from "../../utils/shiftUtils";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export const errorText = err => err?.response?.data?.errorMessage || err?.message;

const conflictDetail = (c, t) =>
  c.type === "leave"
    ? t("on leave")
    : c.type === "offShift"
      ? t("not planned in a shift at that time")
      : c.detail;

const confirmConflicts = (conflicts, t, { intro, okText }) =>
  new Promise(resolve =>
    Modal.confirm({
      title: t("Scheduling conflicts"),
      width: 600,
      content: (
        <div>
          <p>{intro}</p>
          <ul style={{ maxHeight: 260, overflowY: "auto", paddingInlineStart: 18 }}>
            {conflicts.map((c, i) => (
              <li key={i}>
                <strong>{c.employeeName}</strong> · {c.date}: {conflictDetail(c, t)}
              </li>
            ))}
          </ul>
        </div>
      ),
      okText,
      cancelText: t("Cancel"),
      onOk: () => resolve(true),
      onCancel: () => resolve(false)
    })
  );

/**
 * Sends a write with the user's timezone (leave days are counted in it). When the server answers
 * 409 with `conflicts` (warnings it lets the user override), lists them and resends with `force`
 * if the user confirms. Resolves to the response, or null when cancelled. Other errors throw.
 */
export const sendWithConflictCheck = async ({ method, url, body, t, intro, okText }) => {
  const send = extra =>
    axios({
      method,
      url,
      data: { tz: getTimeZone(), ...body, ...extra },
      headers: authHeaders()
    });
  try {
    return await send({});
  } catch (err) {
    const conflicts = err?.response?.status === 409 && err.response.data?.conflicts;
    if (!conflicts) throw err;
    if (!(await confirmConflicts(conflicts, t, { intro, okText }))) return null;
    return send({ force: true });
  }
};

/** An appointment write; double bookings come back as a plain 409 and cannot be overridden. */
export const sendAppointment = (method, path, body, t) =>
  sendWithConflictCheck({
    method,
    url: `${process.env.REACT_APP_BACKEND_URL}/appointments${path}`,
    body,
    t,
    intro: t("These employees are on leave or not planned in a shift at that time:"),
    okText: t("Book anyway")
  });

/** A leave write; approving a leave that hits booked appointments asks first. */
export const sendLeave = (method, path, body, t) =>
  sendWithConflictCheck({
    method,
    url: `${process.env.REACT_APP_BACKEND_URL}/leaves${path}`,
    body,
    t,
    intro: t("The employee has these appointments during the leave. Reschedule them afterwards:"),
    okText: t("Approve anyway")
  });

/**
 * Calls `onChange` whenever an appointment the server says concerns this user (their provider's,
 * their own as a client, or any for admins) is added, changed, confirmed or deleted.
 */
export const useAppointmentsLive = (socket, onChange) => {
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    if (!socket) return undefined;
    const watch = () => socket.emit("appointments:watch", { token: localStorage.getItem("token") });
    const changed = () => latest.current && latest.current();
    socket.on("connect", watch); // rooms are lost on reconnect
    socket.on("appointments:changed", changed);
    if (socket.connected) watch();
    return () => {
      socket.off("connect", watch);
      socket.off("appointments:changed", changed);
      socket.emit("appointments:unwatch");
    };
  }, [socket]);
};
